import type { ReactNode } from 'react';
import { createContext, useContext, useReducer, useEffect } from 'react';
import type {
  AuthContext as IAuthContext,
  AuthState,
  AuthAction,
  User,
  TenantSummary,
  AuthSession,
  RegisterRequest,
  UserPreferences,
  TenantSettings,
  TenantRole } from '../types/auth';
import {
  TENANT_ROLES,
} from '../types/auth';
// Sprint C10 Lane D (C6 #17): clear the cross-app react-query cache on logout
// so any cached node/security/dashboard data tied to user A is invalidated
// before user B re-authenticates on the same browser session.
import { queryClient } from '../lib/react-query';

// Initial state
const initialState: AuthState = {
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload, error: null };
    
    case 'LOGIN_START':
      return { ...state, isLoading: true, error: null };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        session: action.payload.session,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    
    case 'REFRESH_TOKEN':
      return {
        ...state,
        session: action.payload.session,
      };
    
    case 'UPDATE_PROFILE':
      return {
        ...state,
        user: action.payload,
      };
    
    case 'UPDATE_PREFERENCES':
      // The generated User schema (Quiltt aggregation domain) carries no
      // `preferences` field; UI preferences are not part of the real auth
      // contract. The action is accepted for API symmetry but is a no-op on
      // the user record.
      return state;

    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<IAuthContext | null>(null);

// Placeholder-shaped records for the console's non-authenticating stubs. They
// conform to the generated socket-cloud auth schemas the console aliases in
// `types/auth.ts`: the tenant is a `TenantSummary`, the id and name of
// socket-cloud-organization's Tenant, the two fields the session carries;
// `User` is the aggregation-domain user. The console's prior bespoke RBAC
// fields (role/permissions/preferences/tenantRole on the user;
// plan/settings/limits/billing/roles on the tenant) are not carried by any
// real Aaim auth contract and have been removed -- the console is a read-only
// ops surface that does not call a backend for that model.
//
// NEITHER RECORD MAY PRODUCE AUTHENTICATED STATE. `fallbackUser` is no longer
// reachable from `setTokens` or from the session-rehydration effect -- the two
// code paths that remain and dispatch LOGIN_SUCCESS. A LOGIN_SUCCESS carrying
// it made an unauthenticated visitor look authenticated to every consumer of
// this context. Its only remaining callers are `register`, `getTenantUsers`,
// and `updateTenantUser`, none of which dispatch. The third such path, the
// password `login` method, is deleted outright in sso-05.
// `fallbackTenant` additionally backs the `activeTenant` display derivation
// and the tenant-admin stubs; those render a tenant name and grant nothing.
// It carries no plan, status, quota or timestamps: none is known, and
// socket-cloud's Tenant would require values the console would have to invent.
const fallbackTenant: TenantSummary = {
  id: 'tenant-1',
  name: 'SocketCloud',
};

const fallbackUser: User = {
  id: 'user-1',
  email: 'demo@socketcloud.com',
  name: 'Demo User',
  status: 'active',
  metadata: {},
  tenants: [{ id: fallbackTenant.id, name: fallbackTenant.name }],
  createdAt: new Date('2024-01-15').toISOString(),
  updatedAt: new Date().toISOString(),
};

// Derive the session permission list from a /me response body. The
// real backend carries permissions on the tenant membership; some shapes also
// place them on the user. Falls back to an empty list.
function deriveSessionPermissions(
  data:
    | {
        user?: {
          permissions?: unknown;
          tenants?: Array<{ permissions?: unknown }>;
        };
      }
    | null
    | undefined,
): string[] {
  const userPerms = data?.user?.permissions;
  if (Array.isArray(userPerms)) {
    return userPerms as string[];
  }
  const tenantPerms = data?.user?.tenants?.[0]?.permissions;
  if (Array.isArray(tenantPerms)) {
    return tenantPerms as string[];
  }
  return [];
}

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state - rehydrate the session from the HttpOnly cookie.
  useEffect(() => {
    const validateSession = async () => {
      // One-time purge of any legacy bearer/refresh tokens a prior build left
      // in browser storage. The HttpOnly cookie is now the sole auth source of
      // truth; these removes are cleanup only and are never read as auth state.
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
      } catch {
        // storage unavailable -- ignore
      }

      try {
        // Rehydrate the current user from the session cookie. The SPA sends no
        // token; credentials:'include' carries the sc-auth-token cookie and the
        // backend resolves the user from it. Same-origin: the base is '' and the
        // literal is the whole route (api-version-canonical.md).
        const response = await fetch('/api/v1/auth/me', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          const session: AuthSession = {
            sessionId: 'session-' + Date.now(),
            userId: data.user?.id || '',
            permissions: deriveSessionPermissions(data),
            expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 minutes
          };

          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: data.user,
              session,
            },
          });
        } else {
          // No valid cookie session.
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        // Network error or API unavailable.
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    validateSession();
  }, []);

  // Auth methods
  const logout = async (): Promise<void> => {
    // Browser-token-storage hardening: clear the HttpOnly session cookie
    // server-side. credentials:'include' carries sc-auth-token so the backend
    // can clear it; failures are non-fatal (we still drop local state below).
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Network error -- proceed to clear local state regardless.
    }
    // One-time cleanup of any legacy bearer/refresh tokens in browser storage.
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    // Sprint C10 Lane D (C6 #17): invalidate the shared react-query cache so
    // a subsequent login on the same browser does not surface stale cached
    // data from the previous identity. queryClient.clear() removes all
    // cached queries AND in-flight subscriptions; protected routes that
    // re-render after the LOGOUT dispatch will refetch from scratch.
    queryClient.clear();
    dispatch({ type: 'LOGOUT' });

    // Redirect to the console's own login route (App.tsx declares '/login').
    // The prior target was an off-platform host, which dropped the operator
    // out of socket.aaim.com entirely on logout.
    window.location.href = '/login';
  };

  const register = async (data: RegisterRequest): Promise<User> => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const registeredName = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
    const nowIso = new Date().toISOString();
    const newUser: User = {
      ...fallbackUser,
      id: 'new-user-id',
      email: data.email,
      name: registeredName || data.email,
      status: 'pending_verification',
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    return newUser;
  };

  const setTokens = async (_accessToken: string, _refreshTokenValue: string): Promise<void> => {
    // SSO-callback entry point. The SPA stores NO token: the two parameters are
    // retained only for the `setTokens` signature declared on IAuthContext in
    // `src/types/auth.ts`, and are never read, stored, or transmitted. The only
    // thing that can establish a session here is the HttpOnly cookie the
    // backend set, so the identity is rehydrated from /api/v1/auth/me.
    //
    // When /me does not resolve a session, authentication FAILED and the state
    // dispatched is LOGIN_FAILURE. There is no fallback identity: the prior
    // implementation dispatched LOGIN_SUCCESS with the module-level
    // `fallbackUser` and a 24-hour session whenever /me returned non-OK or
    // threw, so an unauthenticated visitor to the callback route rendered the
    // console as an authenticated user.
    let session: AuthSession;
    let user: User;

    try {
      const response = await fetch('/api/v1/auth/me', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      const resolvedId: unknown = data?.user?.id;
      if (typeof resolvedId !== 'string' || resolvedId.length === 0) {
        // A 200 that carries no user id resolves no identity either. Substituting
        // an invented id here was the same fabrication in a narrower branch.
        throw new Error('Authentication failed');
      }

      user = data.user as User;
      session = {
        sessionId: 'session-' + Date.now(),
        userId: resolvedId,
        permissions: deriveSessionPermissions(data),
        expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 minutes
      };
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : 'Authentication failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: message });
      throw new Error(message);
    }

    dispatch({ type: 'LOGIN_SUCCESS', payload: { user, session } });
  };

  const updateProfile = async (data: Partial<User>): Promise<User> => {
    if (!state.user) {
throw new Error('No user logged in');
}
    
    const updatedUser = { ...state.user, ...data };
    dispatch({ type: 'UPDATE_PROFILE', payload: updatedUser });
    return updatedUser;
  };

  const updatePreferences = async (preferences: Partial<UserPreferences>): Promise<UserPreferences> => {
    if (!state.user) {
throw new Error('No user logged in');
}

    // The generated User schema carries no preferences field, so there is no
    // stored baseline to merge against; echo the requested partial back. The
    // dispatch is a no-op (see UPDATE_PREFERENCES reducer case).
    const updatedPreferences = { ...preferences } as UserPreferences;
    dispatch({ type: 'UPDATE_PREFERENCES', payload: updatedPreferences });
    return updatedPreferences;
  };

  // Permission checks read the canonical `AuthSession.permissions` string[]
  // (the only permission-bearing field on the real auth contract). The
  // generated aggregation `User` carries no role/permission objects, so the
  // prior user.role / user.permissions / user.tenantRole reads are gone.
  const checkPermission = (resource: string, action: string): boolean => {
    const permissionStr = `${resource}:${action}`;
    return state.session?.permissions?.includes(permissionStr) || false;
  };

  const hasPermission = (permission: string): boolean => {
    return state.session?.permissions?.includes(permission) || false;
  };

  // The real auth contract carries no role objects on the user/session beyond
  // the permission strings above. Role-name checks are treated as
  // permission-prefix membership (e.g. a session granted owner-scoped
  // permissions). TENANT_ROLES remains the canonical role-name vocabulary.
  const hasRole = (roleName: string): boolean => {
    return state.session?.permissions?.some((p) => p.startsWith(`${roleName}:`)) || false;
  };

  const isTenantOwner = (): boolean => {
    return hasRole(TENANT_ROLES.OWNER);
  };

  const isTenantAdmin = (): boolean => {
    return isTenantOwner() || hasRole(TENANT_ROLES.ADMIN);
  };

  // Tenant management methods. The console does not call a backend for tenant
  // administration; these return the fallback-shaped record merged with the
  // requested partial so callers compile against the generated contracts.
  const updateTenant = async (data: Partial<TenantSummary>): Promise<TenantSummary> => {
    return { ...fallbackTenant, ...data };
  };

  const updateTenantSettings = async (settings: Partial<TenantSettings>): Promise<TenantSettings> => {
    // TenantSettings is a console-local view-model (not on the generated
    // Tenant contract). Return a minimal real-shaped baseline merged with the
    // requested partial.
    const baseline: TenantSettings = {
      allowUserRegistration: false,
      requireEmailVerification: true,
      passwordPolicy: {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: true,
        maxAge: 90,
        preventReuse: 5,
      },
      sessionTimeout: 480,
      mfaRequired: false,
      allowedAuthProviders: [],
      customDomainEnabled: false,
      auditLogging: true,
      dataRetentionDays: 365,
    };
    return { ...baseline, ...settings };
  };

  const inviteUser = async (_email: string, _roleId: string): Promise<void> => {
    // No backend invitation flow on the console ops surface.
  };

  const getTenantUsers = async (): Promise<User[]> => {
    return [fallbackUser];
  };

  const updateTenantUser = async (_userId: string, data: Partial<User>): Promise<User> => {
    return { ...fallbackUser, ...data };
  };

  const deactivateTenantUser = async (_userId: string): Promise<void> => {
    // No backend deactivation flow on the console ops surface.
  };

  const createTenantRole = async (role: Omit<TenantRole, 'id' | 'createdAt' | 'updatedAt'>): Promise<TenantRole> => {
    const nowIso = new Date().toISOString();
    return {
      ...role,
      id: 'new-role-id',
      createdAt: nowIso,
      updatedAt: nowIso,
    };
  };

  const updateTenantRole = async (roleId: string, data: Partial<TenantRole>): Promise<TenantRole> => {
    const nowIso = new Date().toISOString();
    // Conform to the generated TenantRole contract (no isOwner/isDefault/createdBy
    // fields; permissions is a string[]; isSystem is required).
    const baseline: TenantRole = {
      id: roleId,
      tenantId: fallbackTenant.id,
      name: 'member',
      description: 'Tenant member',
      permissions: [],
      isSystem: false,
      createdAt: nowIso,
      updatedAt: nowIso,
    };
    return { ...baseline, ...data };
  };

  const deleteTenantRole = async (_roleId: string): Promise<void> => {
    // No backend role-deletion flow on the console ops surface.
  };

  const getTenantRoles = async (): Promise<TenantRole[]> => {
    const nowIso = new Date().toISOString();
    return [
      {
        id: 'tenant-role-1',
        tenantId: fallbackTenant.id,
        name: 'member',
        description: 'Tenant member',
        permissions: [],
        isSystem: false,
        createdAt: nowIso,
        updatedAt: nowIso,
      },
    ];
  };

  // Derive the active tenant from the authenticated user when available. The
  // user.tenants entry carries { id, name }; missing fields fall back to the
  // fallbackTenant so the console always has a tenant id and name to render.
  const liveTenant = state.user?.tenants?.[0] as { id?: string; name?: string } | undefined;
  const activeTenant: TenantSummary = liveTenant
    ? {
        ...fallbackTenant,
        id: liveTenant.id || fallbackTenant.id,
        name: liveTenant.name || fallbackTenant.name,
      }
    : fallbackTenant;

  const contextValue: IAuthContext = {
    user: state.user,
    tenant: activeTenant,
    currentTenant: activeTenant,
    currentUser: state.user,
    session: state.session,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    logout,
    register,
    setTokens,
    updateProfile,
    updatePreferences,
    checkPermission,
    hasPermission,
    hasRole,
    isTenantOwner,
    isTenantAdmin,
    updateTenant,
    updateTenantSettings,
    inviteUser,
    getTenantUsers,
    getUsersForTenant: getTenantUsers,
    updateTenantUser,
    deactivateTenantUser,
    createTenantRole,
    updateTenantRole,
    deleteTenantRole,
    getTenantRoles,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): IAuthContext {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;